import { useState, useEffect, useCallback } from 'react';
import { useTickets } from '@/hooks/useTickets';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { Link } from 'react-router-dom';
import { MagnifyingGlass, Ticket, ChatCircle } from '@phosphor-icons/react';
import { formatRelativeTime } from '@/lib/utils';
import useAppStore from '@/stores/appStore';
import {
  AgentTicketFilterPopover,
  ActiveFilterChips,
  DEFAULT_FILTERS,
} from '@/components/agent/AgentTicketFilterPopover';

// --- Comment highlight helpers (localStorage-based) ---
const COMMENT_READ_KEY = 'resolv_ticket_comment_read';

const getCommentReadMap = () => {
  try {
    return JSON.parse(localStorage.getItem(COMMENT_READ_KEY) || '{}');
  } catch {
    return {};
  }
};

const markCommentRead = (ticketId, lastCommentAt) => {
  const map = getCommentReadMap();
  map[ticketId] = lastCommentAt;
  localStorage.setItem(COMMENT_READ_KEY, JSON.stringify(map));
};

const hasUnreadComment = (ticketId, lastCommentAt) => {
  if (!lastCommentAt) return false;
  const map = getCommentReadMap();
  const lastRead = map[ticketId];
  if (!lastRead) return true;
  return new Date(lastCommentAt) > new Date(lastRead);
};

export const AgentMyTickets = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('open'); // 'open' | 'approved' | 'ongoing' | 'completed' | 'closed'
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const currentUser = useAppStore((state) => state.currentUser);
  const viewAsUser = useAppStore((state) => state.viewAsUser);
  const effectiveUser = viewAsUser || currentUser;
  const { data: tickets, isLoading } = useTickets(undefined, { refetchInterval: 30000 });

  // Filter to tickets taken/processed by this agent
  const myTickets = tickets?.data?.filter((ticket) => ticket.agent_id === effectiveUser?.id);

  // Reset page when switching tabs, searching, or filtering
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, filters]);

  // Tab filtering according to strict lifecycle stages:
  // - Open: Picked up by agent, not yet approved
  // - Approved: Approved by agent, pending FOC technician assignment
  // - Ongoing: Technician assigned & job in progress
  // - Completed: Completed / Rejected
  // - Closed: Closed
  const getTabTickets = () => {
    if (!myTickets) return [];
    switch (activeTab) {
      case 'open':
        return myTickets.filter((t) => t.status === 'picked_up');
      case 'approved':
        return myTickets.filter((t) => t.status === 'approved');
      case 'ongoing':
        return myTickets.filter((t) => ['assigned', 'in_progress'].includes(t.status));
      case 'completed':
        return myTickets.filter((t) => ['completed', 'rejected'].includes(t.status));
      case 'closed':
        return myTickets.filter((t) => t.status === 'closed');
      default:
        return [];
    }
  };

  const tabTickets = getTabTickets();

  // Apply multi-criteria filters + search
  const filteredTickets = tabTickets.filter((ticket) => {
    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchNumber = ticket.ticket_number?.toLowerCase().includes(q);
      const matchCustomer = ticket.customer_name?.toLowerCase().includes(q);
      const matchPhone = ticket.customer_phone?.toLowerCase().includes(q);
      const matchProduct = ticket.product_name?.toLowerCase().includes(q);
      const matchSerial = ticket.serial_number?.toLowerCase().includes(q);
      if (!matchNumber && !matchCustomer && !matchPhone && !matchProduct && !matchSerial) {
        return false;
      }
    }

    // Priority Filter
    if (filters.priority !== 'all' && ticket.priority !== filters.priority) {
      return false;
    }

    // SLA Status Filter
    if (filters.slaStatus !== 'all' && ticket.sla_deadline) {
      const now = new Date();
      const deadline = new Date(ticket.sla_deadline);
      const diffMins = (deadline - now) / 60000;

      if (filters.slaStatus === 'breached' && diffMins > 0) return false;
      if (filters.slaStatus === 'near_breach' && (diffMins <= 0 || diffMins > 60)) return false;
      if (filters.slaStatus === 'on_track' && diffMins <= 60) return false;
    }

    // Warranty Status Filter
    if (filters.warrantyStatus !== 'all') {
      const wStatus = (ticket.warranty_status || '').toLowerCase();
      if (filters.warrantyStatus === 'active' && !wStatus.includes('active')) return false;
      if (filters.warrantyStatus === 'expired' && !wStatus.includes('expired')) return false;
    }

    // Date Range Filter
    if (filters.dateRange !== 'all' && ticket.created_at) {
      const created = new Date(ticket.created_at);
      const now = new Date();
      if (filters.dateRange === 'today') {
        if (created.toDateString() !== now.toDateString()) return false;
      } else if (filters.dateRange === 'last_7_days') {
        if (now - created > 7 * 86400 * 1000) return false;
      } else if (filters.dateRange === 'last_30_days') {
        if (now - created > 30 * 86400 * 1000) return false;
      }
    }

    return true;
  });

  // Sorting
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    if (filters.sortBy === 'priority_desc') {
      const priorityOrder = { urgent: 0, normal: 1, low: 2 };
      return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
    }
    if (filters.sortBy === 'created_asc') {
      return new Date(a.created_at) - new Date(b.created_at);
    }
    if (filters.sortBy === 'sla_urgency') {
      return new Date(a.sla_deadline || 0) - new Date(b.sla_deadline || 0);
    }
    // Default: Created Date (Newest First)
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Pagination slicing
  const totalTickets = sortedTickets.length;
  const totalPages = Math.ceil(totalTickets / pageSize);
  const paginatedTickets = sortedTickets.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Mark ticket comments as read when clicking on a ticket
  const handleTicketClick = useCallback((ticket) => {
    if (ticket.last_comment_at) {
      markCommentRead(ticket.id, ticket.last_comment_at);
    }
  }, []);

  return (
    <DashboardLayout role="agent">
      <div className="page-padding max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black tracking-tight mb-2">My Tickets</h1>
          <p className="text-muted-foreground">Review tickets you have processed, approved, or closed</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-6 gap-2 overflow-x-auto">
          {[
            {
              id: 'open',
              label: `Open`,
              count: myTickets?.filter((t) => t.status === 'picked_up').length || 0,
            },
            {
              id: 'approved',
              label: `Approved`,
              count: myTickets?.filter((t) => t.status === 'approved').length || 0,
            },
            {
              id: 'ongoing',
              label: `Ongoing`,
              count: myTickets?.filter((t) => ['assigned', 'in_progress'].includes(t.status)).length || 0,
            },
            {
              id: 'completed',
              label: `Completed`,
              count: myTickets?.filter((t) => ['completed', 'rejected'].includes(t.status)).length || 0,
            },
            {
              id: 'closed',
              label: `Closed`,
              count: myTickets?.filter((t) => t.status === 'closed').length || 0,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Search & Filter */}
        <div className="flex gap-4 mb-3">
          <div className="flex-1 relative">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder="Search by ticket number, customer, or product..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <AgentTicketFilterPopover filters={filters} setFilters={setFilters} />
        </div>

        {/* Active Filter Chips */}
        <ActiveFilterChips filters={filters} setFilters={setFilters} />

        {/* Tickets Table */}
        {isLoading ? (
          <LoadingSpinner />
        ) : paginatedTickets?.length > 0 ? (
          <div className="space-y-3">
            {paginatedTickets.map((ticket) => {
              const unread = hasUnreadComment(ticket.id, ticket.last_comment_at);
              return (
                <Link
                  key={ticket.id}
                  to={`/ops/agent/tickets/${ticket.id}`}
                  onClick={() => handleTicketClick(ticket)}
                >
                  <Card
                    className={`p-4 hover:bg-muted/30 transition-all cursor-pointer border ${
                      unread ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''
                    }`}
                    data-testid={`ticket-row-${ticket.ticket_number}`}
                  >
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Ticket Info */}
                      <div className="col-span-3">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-bold">{ticket.ticket_number}</p>
                          {unread && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold bg-blue-100 text-blue-700 animate-pulse"
                              title="New note added"
                            >
                              <ChatCircle size={10} weight="fill" /> New
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{ticket.customer_name}</p>
                      </div>

                      {/* Product */}
                      <div className="col-span-3">
                        <p className="font-medium text-sm">{ticket.product_name}</p>
                        <p className="text-xs text-muted-foreground">{ticket.issue_category}</p>
                      </div>

                      {/* Status & Priority */}
                      <div className="col-span-2 flex flex-col gap-1 items-start">
                        <PriorityBadge priority={ticket.priority} />
                        <StatusBadge status={ticket.status} />
                      </div>

                      {/* SLA Timer */}
                      <div className="col-span-2">
                        <SlaTimer deadline={ticket.sla_deadline} status={ticket.status} />
                      </div>

                      {/* Relative Time */}
                      <div className="col-span-2 text-right">
                        <p className="text-xs text-muted-foreground">
                          {formatRelativeTime(ticket.created_at)}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center border-dashed">
            <p className="text-muted-foreground">No tickets in this section</p>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="flex items-center px-3 text-sm text-muted-foreground font-medium">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
